/**
 * Pure-JS SHA-256 — sync, dependency-free.
 *
 * Why pure JS (no node:crypto, no Web Crypto):
 *   This module is consumed in three runtimes:
 *     1. Browser (CSV preview before upload).
 *     2. Supabase Edge Function / Deno (the chunked import worker).
 *     3. Node 20+ via Vitest.
 *   node:crypto is sync but Web Crypto's SubtleCrypto.digest is
 *   async — using it would force every row-hash call site (parsers,
 *   dedupe) to become async and ripple through every test. A small
 *   sync FIPS 180-4 SHA-256 implementation avoids that whole class
 *   of plumbing.
 *
 * Performance note:
 *   ~10 MB/s on modern V8 for short inputs. For the Slice 2 use
 *   case (~50–500 byte canonical strings per row, batches up to a
 *   few hundred thousand rows in a chunked worker), this is well
 *   below the network round-trip budget. If a future ingest path
 *   needs faster, the edge worker can plug in `node:crypto`
 *   directly at the call site without changing this module's API.
 *
 * Correctness:
 *   This is a direct transcription of FIPS 180-4 §6.2.2 (SHA-256).
 *   Cross-checked against a published vector set in
 *   tests/unit/lib/shopHistoryImport/sha256.test.ts (the standard
 *   "abc" + empty-string + 1MB-of-'a' suite).
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotr(n: number, x: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

function utf8Encode(input: string): Uint8Array {
  // Browser/Deno/Node all expose TextEncoder.
  return new TextEncoder().encode(input);
}

/** SHA-256 of a UTF-8 string. Returns 64-char lowercase hex. */
export function sha256Hex(input: string): string {
  const data = utf8Encode(input);
  const bitLen = data.length * 8;

  // Pre-processing: append 0x80, pad with zeros until length ≡ 56 (mod 64),
  // then append the 64-bit big-endian bit length.
  const padded = new Uint8Array(
    data.length + 1 + ((56 - ((data.length + 1) % 64) + 64) % 64) + 8,
  );
  padded.set(data, 0);
  padded[data.length] = 0x80;

  // 64-bit length (big-endian). bitLen fits in 53 bits in JS — split into
  // hi/lo 32-bit words via division to avoid bitwise overflow.
  const hi = Math.floor(bitLen / 0x100000000);
  const lo = bitLen >>> 0;
  const lenOffset = padded.length - 8;
  padded[lenOffset + 0] = (hi >>> 24) & 0xff;
  padded[lenOffset + 1] = (hi >>> 16) & 0xff;
  padded[lenOffset + 2] = (hi >>> 8) & 0xff;
  padded[lenOffset + 3] = hi & 0xff;
  padded[lenOffset + 4] = (lo >>> 24) & 0xff;
  padded[lenOffset + 5] = (lo >>> 16) & 0xff;
  padded[lenOffset + 6] = (lo >>> 8) & 0xff;
  padded[lenOffset + 7] = lo & 0xff;

  // Initial hash values (FIPS 180-4 §5.3.3).
  const H = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);

  const W = new Uint32Array(64);
  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    for (let i = 0; i < 16; i++) {
      const o = chunk + i * 4;
      W[i] =
        ((padded[o] << 24) |
          (padded[o + 1] << 16) |
          (padded[o + 2] << 8) |
          padded[o + 3]) >>>
        0;
    }
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(7, W[i - 15]) ^ rotr(18, W[i - 15]) ^ (W[i - 15] >>> 3);
      const s1 = rotr(17, W[i - 2]) ^ rotr(19, W[i - 2]) ^ (W[i - 2] >>> 10);
      W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
    }

    let a = H[0];
    let b = H[1];
    let c = H[2];
    let d = H[3];
    let e = H[4];
    let f = H[5];
    let g = H[6];
    let h = H[7];

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
      const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    H[0] = (H[0] + a) >>> 0;
    H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0;
    H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0;
    H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0;
    H[7] = (H[7] + h) >>> 0;
  }

  let hex = '';
  for (let i = 0; i < 8; i++) {
    hex += H[i].toString(16).padStart(8, '0');
  }
  return hex;
}
